import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

const dateFmt = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
const shortFmt = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' });

export const formatDate = (d: string | Date) => dateFmt.format(new Date(d));
export const formatDateTime = (d: string | Date) => dateTimeFmt.format(new Date(d));
export const formatShort = (d: string | Date) => shortFmt.format(new Date(d));

const rtf = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });
export function formatRelative(d: string | Date) {
  const diff = (new Date(d).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return 'gerade eben';
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 7) return rtf.format(Math.round(diff / 86400), 'day');
  return formatDate(d);
}

export const formatPercent = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(digits)} %`;

export function formatDuration(sec: number | null | undefined) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m ? `${m} min ${String(s).padStart(2, '0')} s` : `${s} s`;
}

/** Notenskala orientiert an Goethe-Prüfungen. */
export function gradeFor(percent: number) {
  if (percent >= 90) return { label: 'sehr gut', tone: 'ok' as const };
  if (percent >= 80) return { label: 'gut', tone: 'ok' as const };
  if (percent >= 70) return { label: 'befriedigend', tone: 'neutral' as const };
  if (percent >= 60) return { label: 'ausreichend', tone: 'warn' as const };
  return { label: 'nicht bestanden', tone: 'bad' as const };
}

export const countWords = (text: string) => text.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu)?.length ?? 0;

export function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Hallo';
  return 'Guten Abend';
}

export const MODE_LABEL: Record<string, string> = {
  UNIT: 'Einheit',
  LEVEL: 'Niveau',
  MIXED: 'Gemischt',
  REVIEW: 'Wiederholung',
  READING: 'Lesen',
  ADAPTIVE: 'Heute lernen',
  CHECK: 'Prüf mich',
  DIAGNOSTIC: 'Einstufungstest',
};

/** Chave local de dia (fuso do navegador). */
export const dayKey = (d: string | Date) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
